import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Spinner from "../ogComponent/spinner";
import Table from "../ogComponent/table";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IconCheck, IconX } from "@tabler/icons-react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

export default function RequestView() {
  const [selectedRequest, setSelectedRequest] = useState("Pending Requests");
  const [product, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const permissions = useSelector((state) => state.emp.permissions);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const header = [
    { name: "siNo", label: "#", active: true },
    { name: "name", label: "Name", active: true },
    { name: "start_date", label: "Date", active: true },
    { name: "leave_type", label: "Leave Type", active: true },
    { name: "total_leave_days", label: "Duration", active: true },
    { name: "status", label: "Status", active: true },
    {
      name: "cancel",
      label: "",
      active:
        (selectedRequest === "Pending Requests" ||
          selectedRequest === "Approved Requests") &&
        permissions.includes("rejectleaverequestID"),
    },
    {
      name: "approved",
      label: "",
      active:
        (selectedRequest === "Pending Requests" ||
          selectedRequest === "Rejected Requests") &&
        permissions.includes("approveleaverequestID"),
    },
  ];

  const handleSelect = (request) => {
    setSelectedRequest(request);
  };

  const formatDateForAPI = (dayjsObj) => {
    return dayjsObj?.isValid?.() ? dayjsObj.format("YYYY-MM-DD") : null;
  };

  const fetchData = async () => {
    setLoading(true);
    setData([]);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No access token found");
      }

      const params = new URLSearchParams();

      const formattedStart = formatDateForAPI(startDate);
      const formattedEnd = formatDateForAPI(endDate);

      if (formattedStart) params.append("start_date", formattedStart);
      if (formattedEnd) params.append("end_date", formattedEnd);

      let baseUrl = "";
      switch (selectedRequest) {
        case "Pending Requests":
          baseUrl = "/api/leavemanagement/leaves/pending_leave/";
          break;
        case "Approved Requests":
          baseUrl = "/api/leavemanagement/leaves/approved_leaves/";
          break;
        case "Rejected Requests":
          baseUrl = "/api/leavemanagement/leaves/rejected_leaves/";
          break;
        default:
          baseUrl = "/api/leavemanagement/leaves/pending_leave/";
      }

      const url = `${process.env.REACT_APP_API}${baseUrl}${params.toString() ? `?${params.toString()}` : ""
        }`;

      console.log("Fetching URL:", url); // ← for debugging

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // IMPORTANT FIX: your API wraps the array inside "data"
      const rawData = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      console.log("Received items:", rawData.length); // ← helpful log

      const formattedData = rawData.map((item, index) => {
        const base = {
          ...item,
          siNo: index + 1,
          status: item.status || "Unknown",
          name: (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={item.employee?.profile_photo || "/assets/user.png"}
                alt="Profile"
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
              {item.employee?.name || "N/A"}
            </div>
          ),
          cancel: null,
          approved: null,
        };

        // Action buttons
        if (selectedRequest === "Pending Requests") {
          if (permissions.includes("rejectleaverequestID")) {
            base.cancel = (
              <button
                className="btn btn-outline-danger d-flex align-items-center"
                style={{ height: "25px" }}
                onClick={(e) =>
                  handleRejectLeave(
                    e,
                    item.employee?.name || "Employee",
                    item.id,
                  )
                }
              >
                <IconX stroke={2} />
              </button>
            );
          }
          if (permissions.includes("approveleaverequestID")) {
            base.approved = (
              <button
                className="btn btn-outline-success d-flex align-items-center"
                style={{ height: "25px" }}
                onClick={(e) =>
                  handleApproveLeave(
                    e,
                    item.employee?.name || "Employee",
                    item.id,
                  )
                }
              >
                <IconCheck stroke={2} />
              </button>
            );
          }
        } else if (selectedRequest === "Approved Requests") {
          if (permissions.includes("rejectleaverequestID")) {
            base.cancel = (
              <button
                className="btn btn-outline-danger d-flex align-items-center"
                style={{ height: "25px" }}
                onClick={(e) =>
                  handleRejectLeave(
                    e,
                    item.employee?.name || "Employee",
                    item.id,
                  )
                }
              >
                <IconX stroke={2} />
              </button>
            );
          }
        } else if (selectedRequest === "Rejected Requests") {
          if (permissions.includes("approveleaverequestID")) {
            base.approved = (
              <button
                className="btn btn-outline-success d-flex align-items-center"
                style={{ height: "25px" }}
                onClick={(e) =>
                  handleApproveLeave(
                    e,
                    item.employee?.name || "Employee",
                    item.id,
                  )
                }
              >
                <IconCheck stroke={2} />
              </button>
            );
          }
        }

        return base;
      });

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.includes("showleaverequestID")) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [selectedRequest, startDate, endDate, permissions]);

  const handleApproveLeave = async (e, employeeName, leaveId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/${leaveId}/approve_leave/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${employeeName}'s leave approved!`);
      fetchData();
    } catch (error) {
      toast.error("Error approving leave");
      console.error(error);
    }
  };

  const handleRejectLeave = async (e, employeeName, leaveId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(
        `${process.env.REACT_APP_API}/api/leavemanagement/leaves/${leaveId}/reject_leave/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.info(`${employeeName}'s leave rejected!`);
      fetchData();
    } catch (error) {
      toast.error("Error rejecting leave");
      console.error(error);
    }
  };

  const handleRowClick = (item) => {
    if (item?.employee?.id && item?.id) {
      navigate(`/user/leaveDetails/${item.employee.id}/${item.id}/`);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="LeaveViewContainer w-100 d-flex flex-column p-3 p-md-4 gap-3 gap-md-4">
      <h3>Leave Requests</h3>

      <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
        <div className="dropdown">
          <button
            className="btn dropdown-toggle text-white border-0"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{
              fontSize: "clamp(12px, 1.4vw, 16px)",
              backgroundColor: "#ba6f01",
              maxHeight: "40px",
              minWidth: "200px",
            }}
          >
            {selectedRequest}
          </button>

          <ul className="dropdown-menu">
            <li>
              <div
                className="dropdown-item"
                onClick={() => handleSelect("Pending Requests")}
              >
                Pending Requests
              </div>
            </li>
            <li>
              <div
                className="dropdown-item"
                onClick={() => handleSelect("Approved Requests")}
              >
                Approved Requests
              </div>
            </li>
            <li>
              <div
                className="dropdown-item"
                onClick={() => handleSelect("Rejected Requests")}
              >
                Rejected Requests
              </div>
            </li>
          </ul>
        </div>

        <div className="accordion-body pb-3 mt-3 mt-sm-0">
          <div className="d-flex gap-3 align-items-center justify-content-end">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                format="DD/MM/YYYY"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="End Date"
                format="DD/MM/YYYY"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
          </div>
        </div>
      </div>

      {permissions.includes("showleaverequestID") ? (
        product.length > 0 ? (
          <Table
            header={header}
            product={product}
            onRowClick={handleRowClick}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        ) : (
          <div
            className="d-flex justify-content-center align-items-center position-relative"
            style={{ minHeight: "50vh" }}
          >
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                backgroundImage: "url(/assets/file.png)",
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                opacity: 0.25,
              }}
            />
            <p className="m-0 fw-light fs-5 position-relative text-muted">
              No Leave Requests Found
            </p>
          </div>
        )
      ) : (
        <div className="alert alert-warning mt-4">
          You do not have permission to view leave requests.
        </div>
      )}
    </div>
  );
}
